import { useState } from 'react';
import { ArrowLeft, PhoneForwarded } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserSelect } from '@/components/shared/record-form';

interface TransferPanelProps {
    readonly onBack: () => void;
    readonly onTransfer: (target: { targetUserId?: string; targetNumber?: string }) => void;
    readonly isTransferring: boolean;
}

type TransferMode = 'user' | 'number';

/** Cold-transfer target picker — an internal teammate or an external E.164 number. */
export function TransferPanel({ onBack, onTransfer, isTransferring }: TransferPanelProps) {
    const [mode, setMode] = useState<TransferMode>('user');
    const [userId, setUserId] = useState('');
    const [number, setNumber] = useState('');

    const canSubmit = mode === 'user' ? Boolean(userId) : Boolean(number.trim());

    const handleSubmit = (): void => {
        if (!canSubmit) return;
        onTransfer(mode === 'user' ? { targetUserId: userId } : { targetNumber: number.trim() });
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-[calc(1rem*var(--voice-scale,1))]">
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={onBack}
                    aria-label="Back to call"
                >
                    <ArrowLeft className="size-4" />
                </Button>
                <p className="text-sm font-semibold tracking-tight text-text-primary">
                    Transfer call
                </p>
            </div>

            <Tabs value={mode} onValueChange={(value) => setMode(value as TransferMode)}>
                <TabsList className="w-full">
                    <TabsTrigger value="user" className="flex-1">
                        Teammate
                    </TabsTrigger>
                    <TabsTrigger value="number" className="flex-1">
                        Number
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {mode === 'user' ? (
                <UserSelect
                    value={userId}
                    onChange={setUserId}
                    disabled={isTransferring}
                    // The widget container is fixed z-1000 — match the other
                    // widget dropdowns (presence, caller-ID) so the portaled
                    // popover paints above it.
                    popoverClassName="z-1001"
                />
            ) : (
                <Input
                    value={number}
                    onChange={(event) => setNumber(event.target.value)}
                    placeholder="+15551234567"
                    disabled={isTransferring}
                />
            )}

            <Button
                type="button"
                className="mt-auto h-[calc(2.375rem*var(--voice-scale,1))] gap-2 rounded-full"
                disabled={!canSubmit || isTransferring}
                onClick={handleSubmit}
            >
                <PhoneForwarded className="size-4" />
                {isTransferring ? 'Transferring…' : 'Transfer'}
            </Button>
        </div>
    );
}
